import {
  ProductItemType,
  PRODUCT_ITEM_TYPES,
  ItemSize,
  ITEM_SIZES,
} from 'jsx/api/generated';
import { ITEM_IDS } from 'jsx/constants/items';
import SubscriptionItemModel from 'jsx/models/subscriptionItem';

// TODO (MJA): Update the interface to `{ id: number }` and use the id
// instead of (type, size, name) to uniquely identify items.

export interface GenericItem {
  type: ProductItemType;
  size: ItemSize | null;
  name: string;
  id?: number;
  price?: number;
}

export const isType = <T extends GenericItem>(
  item: T,
  type: ProductItemType,
): boolean => item.type === type;

export const isSize = <T extends GenericItem>(
  item: T,
  size: ItemSize | null,
): boolean => item.size === size;

export const isSizeless = <T extends GenericItem>(item: T): boolean =>
  item.size === null;

export const nameIncludesRich = <T extends GenericItem>(item: T): boolean =>
  item.name.toLowerCase().includes('rich');

export const nameIncludesDeepCleanse = <T extends GenericItem>(
  item: T,
): boolean => item.name.toLowerCase().includes('deep cleanse');

const filterType = <T extends GenericItem>(
  items: T[],
  type: ProductItemType,
  size: ItemSize | null = null,
) =>
  items.filter(
    (item) => isType(item, type) && (size === null || isSize(item, size)),
  );

type FindType = <T extends GenericItem>(
  items: T[],
  size?: ItemSize | null,
) => T | undefined;

export const findFutureFormula: FindType = (items, size = null) =>
  filterType(items, PRODUCT_ITEM_TYPES.FUTURE_FORMULA, size)[0];

export const findDarkSpotFormula: FindType = (items, size = null) =>
  filterType(items, PRODUCT_ITEM_TYPES.DARK_SPOT_FORMULA, size)[0];

export const findLipBalm: FindType = (items, size = null) =>
  filterType(items, PRODUCT_ITEM_TYPES.LIP_BALM, size)[0];

export const findSpfLipBalm = (items: SubscriptionItemModel[]) =>
  items.find((item) => item.itemId === ITEM_IDS.spfLipBalm);

export const findMicellarMakeupRemover: FindType = (items, size = null) =>
  filterType(items, PRODUCT_ITEM_TYPES.MAKEUP_REMOVER, size)[0];

export const findCustomFormula: FindType = (items, size = null) =>
  filterType(items, PRODUCT_ITEM_TYPES.PRESCRIPTION_BOTTLE, size)[0];

export const findCleanser: FindType = (items, size = null) =>
  filterType(items, PRODUCT_ITEM_TYPES.CLEANSER, size)[0];

export const findHydrocolloidBandage: FindType = (items, size = null) =>
  filterType(items, PRODUCT_ITEM_TYPES.HYDROCOLLOID, size)[0];

export const findAcneBodyWash: FindType = (items, size = null) =>
  filterType(items, PRODUCT_ITEM_TYPES.ACNE_BODY_WASH, size)[0];

export const findGelMoisturizer: FindType = (items, size = null) =>
  filterType(items, PRODUCT_ITEM_TYPES.MOISTURIZER, size).find(
    (item) => !nameIncludesRich(item),
  );

export const findRichMoisturizer: FindType = (items, size = null) =>
  filterType(items, PRODUCT_ITEM_TYPES.MOISTURIZER, size).find((item) =>
    nameIncludesRich(item),
  );

export const findDeepCleanse = (items: SubscriptionItemModel[]) =>
  items.find(
    (item) =>
      item.itemId === ITEM_IDS.smallDeepCleanse ||
      item.itemId === ITEM_IDS.largeDeepCleanse,
  );

export const findAcneCleanser = (items: SubscriptionItemModel[]) =>
  items.find(
    (item) =>
      item.itemId === ITEM_IDS.largeAcneCleanser ||
      item.itemId === ITEM_IDS.smallAcneCleanser,
  );

export const includesCMSet = <T extends GenericItem>(items: T[]): boolean => {
  const hasCustomFormula = !!findCustomFormula(items);
  const hasCleanser = !!findCleanser(items);
  const hasGelMoisturizer = !!findGelMoisturizer(items);

  return hasCustomFormula && hasCleanser && hasGelMoisturizer;
};

export const includesCRMSet = <T extends GenericItem>(items: T[]): boolean => {
  const hasCustomFormula = !!findCustomFormula(items);
  const hasCleanser = !!findCleanser(items);
  const hasRichMoisturizer = !!findRichMoisturizer(items);

  return hasCustomFormula && hasCleanser && hasRichMoisturizer;
};

export const findItem = <T extends GenericItem>(
  item: GenericItem,
  items: T[],
): T => {
  const resultItem = items.find(
    (listItem) =>
      isType(item, listItem.type) &&
      isSize(item, listItem.size) &&
      item.name === listItem.name,
  );

  if (!resultItem) {
    throw new Error(`Cannot find the specified item (${item.name})`);
  }

  return resultItem;
};

export const findSmallOfItem = <T extends GenericItem>(
  item: GenericItem,
  items: T[],
): T => {
  let smallItem;

  if (item.type === PRODUCT_ITEM_TYPES.MOISTURIZER) {
    const shouldIncludeRich = nameIncludesRich(item);
    smallItem = items.find(
      (searchItem: T) =>
        isType(searchItem, item.type) &&
        isSize(searchItem, ITEM_SIZES.SMALL) &&
        nameIncludesRich(searchItem) === shouldIncludeRich,
    );
  } else if (item.type === PRODUCT_ITEM_TYPES.PRESCRIPTION_BOTTLE) {
    const shouldIncludeDeepCleanse = nameIncludesDeepCleanse(item);
    smallItem = items.find(
      (searchItem: T) =>
        isType(searchItem, item.type) &&
        isSize(searchItem, ITEM_SIZES.SMALL) &&
        nameIncludesDeepCleanse(searchItem) === shouldIncludeDeepCleanse,
    );
  } else {
    smallItem = items.find(
      (searchItem: T) =>
        isType(searchItem, item.type) && isSize(searchItem, ITEM_SIZES.SMALL),
    );
  }

  if (!smallItem) {
    throw new Error(
      `Cannot find the small version of the specified item (${item.name})`,
    );
  }

  return smallItem;
};

export const findTrialVersionOfItem = <T extends GenericItem>(
  item: GenericItem,
  items: T[],
): T => {
  return isSizeless(item)
    ? findItem(item, items)
    : findSmallOfItem(item, items);
};

interface PricedItem extends GenericItem {
  price: number;
}

/**
 *  Get the total charge cost for the items.
 *  This is how much the patient will be charged on whatever basis they are charge (monthly, bi-monthly, etc).
 */
export const totalChargeCost = (items: PricedItem[]): number =>
  items.map((item) => item.price).reduce((carry, price) => carry + price, 0);

/**
 * Determine the monthly cost for the items.  All of the items should be of the same size or sizeless.
 * TODO: This function assumes that sizeless items can only be charged on a bimonthly cadence,
 * which is true right now because the only sizeless items are OTC items
 * that can only be subscribed to as part of a bimonthly subscription.
 * If that stops being the case, that assumption won't be a safe one anymore
 * and this function and its consumers will probably need to be rethought a bit.
 */
export const monthlyCost = (items: PricedItem[]): number => {
  if (items.length === 0) {
    return 0;
  }
  const isChargedBimonthly =
    isSizeless(items[0]) || isSize(items[0], ITEM_SIZES.LARGE);
  const totalCharge = totalChargeCost(items);
  return isChargedBimonthly ? totalCharge / 2 : totalCharge;
};

/**
 * Determine the shipping cost for the set of items.  All of the items should be of the same size or sizeless.
 */
export const shippingCost = (items: PricedItem[]): number => {
  if (items.length === 0) {
    return 0;
  }
  const isShippedBimonthly =
    isSizeless(items[0]) || isSize(items[0], ITEM_SIZES.LARGE);
  return isShippedBimonthly ? 0 : 4.95; // todo-intro-offer: Will need to change this logic for intro offers. Instead of checking for bi-monthly, need to check whether everything in the shipment is free or not. This must be changed before we go to the first shipment being a two-month shipment as well.
};

/**
 * Determine if a patient receives free shipping for their subscription.
 */
export const hasFreeShipping = (items: PricedItem[]): boolean => {
  return shippingCost(items) === 0;
};
